import { useState, useEffect, useCallback, useRef } from 'react';
import { isTauri } from '../utils/platform';

interface UpdateInfo {
  version: string;
  body: string | null;
}

export function useTauriUpdater() {
  const [updateAvailable, setUpdateAvailable] = useState<UpdateInfo | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const updateRef = useRef<{ download: () => Promise<void>; install: () => Promise<void> } | null>(null);

  const checkForUpdates = useCallback(async () => {
    if (!isTauri()) return;

    try {
      const { check } = await import('@tauri-apps/plugin-updater');
      const update = await check();

      if (update) {
        updateRef.current = update;
        setUpdateAvailable({
          version: update.version,
          body: update.body ?? null,
        });
      }
    } catch (e) {
      console.warn('[Tauri updater] check failed:', e);
    }
  }, []);

  const installUpdate = useCallback(async () => {
    const update = updateRef.current;
    if (!update) return;

    setIsUpdating(true);
    try {
      await update.download();
      await update.install();

      const { relaunch } = await import('@tauri-apps/plugin-process');
      await relaunch();
    } catch (e) {
      console.error('[Tauri updater] install failed:', e);
      setIsUpdating(false);
    }
  }, []);

  useEffect(() => {
    if (!isTauri()) return;

    // Check after 5 seconds
    const initialTimeout = setTimeout(checkForUpdates, 5_000);

    // Then check every 30 minutes
    const interval = setInterval(checkForUpdates, 30 * 60 * 1_000);

    return () => {
      clearTimeout(initialTimeout);
      clearInterval(interval);
    };
  }, [checkForUpdates]);

  return { updateAvailable, isUpdating, installUpdate };
}
